/*
  Calming Care 3D — scroll-pinned hero section.

  Two custom elements:
    <wp-calming-3d>   — the section shell. Reads scroll, updates:
                          - bullet reveals (opacity + translateY)
                          - scroll indicator fill
                          - progress attribute on <wp-3d-spin>
    <wp-3d-spin>      — the bottle spinner. Loads three.js dynamically,
                        renders a transparent scene, auto-frames the model,
                        turns it continuously with scroll-linked bonus.

  Libraries load once per page from esm.sh (three@0.184.0 per handoff).
  Rendering only runs while intersecting the viewport.
  Respects prefers-reduced-motion: no autoturn, no sway; scroll-linked
  rotation preserved.
*/
(function () {
  const THREE_URL = 'https://esm.sh/three@0.184.0';
  const GLTF_URL = 'https://esm.sh/three@0.184.0/examples/jsm/loaders/GLTFLoader.js';

  const prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  /* =========================================================
     <wp-calming-3d> — the section shell + scroll driver.
     ========================================================= */
  class WpCalming3d extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.bullets = Array.from(this.querySelectorAll('.cc3d__bullet'));
      this.indicatorFill = this.querySelector('.cc3d__indicator-fill');
      this.spin = this.querySelector('wp-3d-spin');

      // Fade the spinner in once its model is ready.
      if (this.spin) {
        this.spin.addEventListener('model-ready', () => {
          this.spin.classList.add('is-ready');
          this.classList.add('model-ready');
        });
      }

      // Mobile skips the scroll-linked bullet reveal, indicator, and spin
      // bonus — the model just autoturns and everything else sits static.
      // The media query re-evaluates on resize so switching orientation
      // or dragging the DevTools boundary flips modes cleanly.
      this._mql = window.matchMedia('(max-width: 899px)');
      this._onBreakpoint = this._syncMode.bind(this);
      this._mql.addEventListener
        ? this._mql.addEventListener('change', this._onBreakpoint)
        : this._mql.addListener(this._onBreakpoint);

      this._onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);

      this._syncMode();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      if (this._mql) {
        this._mql.removeEventListener
          ? this._mql.removeEventListener('change', this._onBreakpoint)
          : this._mql.removeListener(this._onBreakpoint);
      }
    }

    _syncMode() {
      const mobile = !!(this._mql && this._mql.matches);
      this._mobile = mobile;

      if (mobile) {
        // Clear any inline styles the desktop path may have written so CSS
        // defaults (opacity: 1, transform: none) take over, and reset the
        // spinner to zero scroll bonus so it purely autoturns.
        this.bullets.forEach((b) => {
          b.style.opacity = '';
          b.style.transform = '';
        });
        if (this.indicatorFill) this.indicatorFill.style.transform = '';
        if (this.spin) this.spin.setAttribute('progress', '0');
        return;
      }

      // Desktop mode — kick a scroll pass so bullets/indicator/spin catch up
      // to whatever scroll position we're already at.
      this._onScroll();
    }

    _onScroll() {
      if (this._mobile) return;
      const rect = this.getBoundingClientRect();
      const total = Math.max(1, rect.height - window.innerHeight);
      const p = clamp01(-rect.top / total);
      this._progress = p;
      this._applyProgress(p);
    }

    _applyProgress(p) {
      // Bullets stay fully opaque — the scroll-linked reveal read as "faded"
      // on shorter viewports where the reveal window barely fires. Matches
      // the mobile presentation the client validated.
      if (this.indicatorFill) {
        this.indicatorFill.style.transform = `scaleX(${p.toFixed(4)})`;
      }

      if (this.spin) {
        this.spin.setAttribute('progress', p.toFixed(4));
      }
    }
  }

  if (!customElements.get('wp-calming-3d')) {
    customElements.define('wp-calming-3d', WpCalming3d);
  }

  /* =========================================================
     <wp-3d-spin> — three.js viewer.
     Port of the handoff's spin-model.js with dynamic ESM imports.
     ========================================================= */
  class Wp3dSpin extends HTMLElement {
    static get observedAttributes() {
      return ['progress'];
    }

    attributeChangedCallback(name, _old, value) {
      if (name === 'progress') this._progress = parseFloat(value) || 0;
    }

    async connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._progress = parseFloat(this.getAttribute('progress')) || 0;

      // Pick the correct model URL for the current viewport.
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const desktopSrc = this.dataset.src || '';
      const mobileSrc = this.dataset.srcMobile || desktopSrc;
      const src = isMobile && mobileSrc ? mobileSrc : desktopSrc;
      if (!src) return;

      this.style.display = 'block';
      this.style.position = 'relative';
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'display:block;width:100%;height:100%';
      this.appendChild(canvas);
      this._canvas = canvas;

      let THREE, GLTFLoader;
      try {
        const [threeMod, gltfMod] = await Promise.all([
          import(THREE_URL),
          import(GLTF_URL),
        ]);
        THREE = threeMod;
        GLTFLoader = gltfMod.GLTFLoader;
      } catch (err) {
        console.error('[wp-3d-spin] failed to load three.js', err);
        return;
      }
      if (this._disposed) return;
      this._THREE = THREE;

      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
      });
      renderer.setPixelRatio(Math.min(Math.max(window.devicePixelRatio || 1, 2), 3));
      renderer.setClearColor(0x000000, 0);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      this._renderer = renderer;

      const scene = new THREE.Scene();
      scene.environment = this._buildEnvironment(renderer);
      this._scene = scene;

      const camera = new THREE.PerspectiveCamera(28, 1, 0.01, 100);
      this._camera = camera;

      scene.add(new THREE.HemisphereLight(0xffffff, 0xc2d9df, 0.5));
      const key = new THREE.DirectionalLight(0xffffff, 1.6);
      key.position.set(4, 6, 5);
      scene.add(key);
      const rim = new THREE.DirectionalLight(0xe73881, 0.35);
      rim.position.set(-5, 2, -4);
      scene.add(rim);

      const pivot = new THREE.Group();
      scene.add(pivot);
      this._pivot = pivot;

      // Load model.
      new GLTFLoader().load(
        src,
        (gltf) => {
          if (this._disposed) return;
          this._onModelLoaded(gltf);
        },
        undefined,
        (err) => console.error('[wp-3d-spin] load failed', err)
      );

      // Sizing.
      this._resize = () => {
        const w = this.clientWidth || 1;
        const h = this.clientHeight || 1;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        this._frame();
      };
      this._ro = new ResizeObserver(this._resize);
      this._ro.observe(this);
      this._resize();
      requestAnimationFrame(this._resize);

      // Only render while in viewport (+/- 200px).
      this._visible = true;
      this._io = new IntersectionObserver(
        (entries) => {
          this._visible = entries[0].isIntersecting;
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);

      // rAF loop.
      this._clock = new THREE.Clock();
      const loop = () => {
        if (this._disposed) return;
        this._raf = requestAnimationFrame(loop);
        if (!this._visible || !this._pivot) return;
        this._tick();
      };
      loop();
    }

    disconnectedCallback() {
      this._disposed = true;
      cancelAnimationFrame(this._raf);
      this._ro?.disconnect();
      this._io?.disconnect();
      if (this._pivot && this._model) {
        this._model.traverse((o) => {
          if (o.isMesh) {
            o.geometry?.dispose?.();
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            mats.forEach((m) => {
              if (!m) return;
              for (const k of Object.keys(m)) {
                const v = m[k];
                if (v && v.isTexture) v.dispose();
              }
              m.dispose?.();
            });
          }
        });
      }
      this._renderer?.dispose();
      this._renderer?.forceContextLoss?.();
    }

    /* -------- Environment (PMREM for label gloss) -------- */
    _buildEnvironment(renderer) {
      const THREE = this._THREE;
      const scene = new THREE.Scene();
      const room = new THREE.Mesh(
        new THREE.BoxGeometry(10, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0x9aa3ad, side: THREE.BackSide })
      );
      scene.add(room);
      const light = (w, h, x, y, z, ry, color = 0xffffff, intensity = 1) => {
        const m = new THREE.Mesh(
          new THREE.PlaneGeometry(w, h),
          new THREE.MeshBasicMaterial({
            color: new THREE.Color(color).multiplyScalar(intensity),
          })
        );
        m.position.set(x, y, z);
        m.rotation.y = ry;
        scene.add(m);
      };
      light(4, 3, -4.9, 2, 0, Math.PI / 2, 0xffffff, 6); // key, left
      light(3, 5, 4.9, 1, -1, -Math.PI / 2, 0xc2d9df, 3); // fill, right (pale blue)
      const top = new THREE.Mesh(
        new THREE.PlaneGeometry(6, 1.5),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(0xffffff).multiplyScalar(4),
        })
      );
      top.position.set(0, 4.9, 0);
      top.rotation.x = Math.PI / 2;
      scene.add(top);
      const pmrem = new THREE.PMREMGenerator(renderer);
      const tex = pmrem.fromScene(scene, 0.04).texture;
      pmrem.dispose();
      return tex;
    }

    /* -------- Model loaded: anisotropy, normalise, centre, frame -------- */
    _onModelLoaded(gltf) {
      const THREE = this._THREE;
      const model = gltf.scene;

      // Max anisotropy for crisp label textures.
      const aniso = this._renderer.capabilities.getMaxAnisotropy();
      model.traverse((o) => {
        if (!o.isMesh) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => {
          if (!m) return;
          ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap'].forEach((k) => {
            if (m[k]) {
              m[k].anisotropy = aniso;
              m[k].needsUpdate = true;
            }
          });
        });
      });

      // Compute bounding box from visible product meshes only —
      // skip flat helper meshes (ground/shadow planes).
      model.updateWorldMatrix(true, true);
      const box = new THREE.Box3();
      model.traverse((o) => {
        if (!o.isMesh || !o.visible) return;
        if (/plane|ground|floor|shadow|backdrop/i.test(o.name)) return;
        const b = new THREE.Box3().setFromObject(o);
        const sz = b.getSize(new THREE.Vector3());
        const mx = Math.max(sz.x, sz.y, sz.z);
        const mn = Math.min(sz.x, sz.y, sz.z);
        if (mx > 0 && mn / mx < 0.01) return; // helper plane
        box.union(b);
      });
      if (box.isEmpty()) box.setFromObject(model);

      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const s = 1 / (size.y || 1); // normalise height to 1
      model.scale.setScalar(s);
      model.position.copy(center).multiplyScalar(-s); // apply centering in scaled space

      this._dims = {
        h: 1,
        w: Math.max(size.x, size.z) * s,
      };

      this._pivot.add(model);
      this._model = model;
      this._frame();
      this._ready = true;
      this.dispatchEvent(new CustomEvent('model-ready', { bubbles: true }));
    }

    /* -------- Frame the model with margin for the current aspect -------- */
    _frame() {
      if (!this._camera || !this._dims) return;
      const THREE = this._THREE;
      const { h, w } = this._dims;
      const margin = 1.16;
      const vHalf = THREE.MathUtils.degToRad(this._camera.fov / 2);
      const hHalf = Math.atan(Math.tan(vHalf) * this._camera.aspect);
      const d = Math.max(
        (h * margin / 2) / Math.tan(vHalf),
        (w * margin / 2) / Math.tan(hHalf)
      );
      this._camera.position.set(0, 0, d);
      this._camera.lookAt(0, 0, 0);
    }

    /* -------- Frame tick: rotation + sway + render -------- */
    _tick() {
      const renderer = this._renderer;
      const w = this.clientWidth;
      const h = this.clientHeight;
      const dpr = renderer.getPixelRatio();
      if (
        w &&
        h &&
        (Math.abs(renderer.domElement.width - w * dpr) > 2 ||
          Math.abs(renderer.domElement.height - h * dpr) > 2)
      ) {
        this._resize();
      }

      const t = this._clock.getElapsedTime();
      const p = this._progress;
      const pivot = this._pivot;

      if (prefersReduced) {
        // Static three-quarter view + scroll-linked rotation only.
        pivot.rotation.y = -0.35 + p * Math.PI * 1.1;
        pivot.rotation.z = 0;
        pivot.rotation.x = 0;
        pivot.position.y = 0;
      } else {
        pivot.rotation.y = -0.35 + t * 0.32 + p * Math.PI * 1.1;
        pivot.rotation.z = Math.sin(t * 0.6) * 0.03;
        pivot.rotation.x = Math.sin(t * 0.45) * 0.025;
        pivot.position.y = Math.sin(t * 0.8) * 0.008;
      }

      renderer.render(this._scene, this._camera);
    }
  }

  if (!customElements.get('wp-3d-spin')) {
    customElements.define('wp-3d-spin', Wp3dSpin);
  }

  /* -------- Shopify theme editor re-init hooks -------- */
  document.addEventListener('shopify:section:load', (e) => {
    if (!e.target) return;
    // Custom elements auto-initialise on connectedCallback, so no manual work
    // required here — this listener exists as documentation of the contract.
  });
})();
